import { useEffect } from "react";

/**
 * 직독직해 세부 설정.
 *
 * 학생이 거치는 흐름 그대로 위에서 아래로 놓는다.
 * 단계(무엇을 보여줄지) → 읽기 속도 → 통과 기준 → 읽기 채점.
 *
 * **어떻게 하는지는 적지 않는다.** 무엇을 해 주는지만 쓴다. 어떤 기술로 채점하는지,
 * 어떤 단말에서 되고 안 되는지, 무엇을 모아 어떻게 잇는지는 우리 사정이고, 출제
 * 화면은 누구나 열어 볼 수 있다.
 *
 * **녹음은 고를 수 없다.** 아이 목소리를 남기지 않기로 했다. 켜는 자리를 두면
 * 무엇을 어떻게 모으는지가 출제 화면에 그대로 드러나기도 한다. 학생 쪽 녹음
 * 코드(`useAudioRecorder.js`)는 나중에 서버 전사로 다시 붙일 때를 위해 남겨 두었고,
 * `is_recording_enabled` 가 켜지지 않으므로 돌지 않는다.
 *
 * 단계를 하나도 안 고르면 학생 화면이 열 것이 없으므로, 처음 펼칠 때 셋 다 켜 둔다.
 */
const DEFAULT_SPEECH_RATE = 1;
// 기본은 끊어읽기. 직독직해라는 이름 그대로가 기본이어야 한다.
const DEFAULT_READING_UNIT = "chunk";
// 통과 기준. 받아쓰기가 관사나 복수형을 자주 흘려서 100 을 요구하면 잘 읽은 아이가
// 계속 막힌다. 그래서 기본을 70 으로 둔다.
const DEFAULT_PASSING_SCORE = 70;

// 아이가 소리 내어 말하는 단계. 읽기 채점이 있어야 성립한다.
const SPEAKING_MODE_LIST = ["fluency", "interpretation"];

// 프리미엄 단계. 베타 기간에는 plus 도 눌러볼 수 있어서 막지 않고 알려만 준다.
const PREMIUM_MODE_LIST = SPEAKING_MODE_LIST;
const PREMIUM_NOTICE =
  "베타 기간 plus 이용 선생님들도 체험하실 수 있습니다.";

const PremiumBadge = ({ onClick }) => (
  <span
    onClick={(event) => {
      // 알약을 누르면 단계가 켜지고 꺼진다. 뱃지는 안내만 띄우고 거기서 멈춘다.
      event.stopPropagation();
      onClick();
    }}
    style={{
      marginLeft: "6px",
      padding: "1px 6px",
      borderRadius: "999px",
      backgroundColor: "#0E141B",
      color: "#FFF",
      fontSize: "0.62rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
      verticalAlign: "middle",
    }}
  >
    PREMIUM
  </span>
);

const MODE_DESCRIPTION = {
  listening: "영어를 끊어 들려주고 우리말을 함께 봅니다. 소리 내어 읽지 않습니다",
  fluency: "들은 대로 소리 내어 읽습니다. 얼마나 정확히 읽었는지 채점합니다",
  interpretation: "영어만 보고 우리말로 통역해 말합니다. 뜻이 맞는지 채점합니다",
};

const pillStyle = (isSelected, isReadOnly) => ({
  padding: "9px 16px",
  borderRadius: "999px",
  border: `1px solid ${isSelected ? "#00D9C0" : "#E6EDEC"}`,
  backgroundColor: isSelected ? "#E5FBF8" : "#FFF",
  color: isSelected ? "#00A396" : "#7A8490",
  fontSize: "0.86rem",
  fontWeight: 700,
  cursor: isReadOnly ? "default" : "pointer",
});

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "11px 14px",
  backgroundColor: "#F4F8F7",
  borderRadius: "12px",
};

const ToggleRow = ({ title, description, isOn, onClick, isReadOnly }) => (
  <div style={rowStyle}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: "0.86rem",
          fontWeight: 800,
          color: "#0E141B",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: "0.74rem", color: "#7A8490", marginTop: "1px" }}>
        {description}
      </div>
    </div>
    <div
      onClick={() => !isReadOnly && onClick()}
      style={{
        width: "46px",
        height: "27px",
        borderRadius: "999px",
        backgroundColor: isOn ? "#00D9C0" : "#D6DEE0",
        position: "relative",
        cursor: isReadOnly ? "default" : "pointer",
        transition: "background-color .15s",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "3px",
          left: isOn ? "22px" : "3px",
          width: "21px",
          height: "21px",
          borderRadius: "50%",
          backgroundColor: "#FFF",
          boxShadow: "0 1px 3px rgba(14,20,27,.18)",
          transition: "left .15s",
        }}
      />
    </div>
  </div>
);

const SubTitle = ({ children }) => (
  <div
    style={{
      fontSize: "0.78rem",
      fontWeight: 800,
      color: "#7A8490",
      letterSpacing: "0.02em",
      marginBottom: "7px",
    }}
  >
    {children}
  </div>
);

/** 통과 점수 한 줄. 0 은 점수와 상관없이 넘어가게 두는 것이다. */
const ScoreRow = ({
  label,
  description,
  value,
  scoreList,
  isReadOnly,
  onChange,
}) => (
  <div>
    <div
      style={{
        fontSize: "0.82rem",
        fontWeight: 800,
        color: "#0E141B",
        marginBottom: "2px",
      }}
    >
      {label}
    </div>
    <div
      style={{ fontSize: "0.74rem", color: "#7A8490", marginBottom: "7px" }}
    >
      {value === 0 ? "점수와 상관없이 넘어갑니다" : description}
    </div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
      {scoreList.map((score) => (
        <div
          key={score}
          onClick={() => !isReadOnly && onChange(score)}
          style={{
            ...pillStyle(value === score, isReadOnly),
            padding: "7px 12px",
            fontSize: "0.8rem",
          }}
        >
          {score}
        </div>
      ))}
    </div>
  </div>
);

const DirectTranslationSelection = ({
  allTypes,
  selectedTypes,
  setSelectedTypes,
}) => {
  const isReadOnly = setSelectedTypes === undefined;

  const typeDefinition = allTypes.find(
    (item) => item.key === "direct_translation"
  );
  const allOptions = typeDefinition?.options || [];
  const allSpeechRates = typeDefinition?.speechRates || [];
  const allReadingUnits = typeDefinition?.readingUnits || [];
  const allPassingScores = typeDefinition?.passingScores || [];

  const selected = selectedTypes.find(
    (item) => item.key === "direct_translation"
  );
  const selectedOptions = selected?.options || [];
  const speechRate = selected?.speech_rate ?? DEFAULT_SPEECH_RATE;
  const readingUnit = selected?.reading_unit ?? DEFAULT_READING_UNIT;
  const isPronunciationCheckEnabled = Boolean(
    selected?.is_pronunciation_check_enabled
  );
  const fluencyPassingScore =
    selected?.fluency_passing_score ?? DEFAULT_PASSING_SCORE;
  const interpretationPassingScore =
    selected?.interpretation_passing_score ?? DEFAULT_PASSING_SCORE;
  // 그 단계를 안 고르면 통과 기준을 물어볼 이유가 없다.
  const hasFluency = selectedOptions.some((item) => item.key === "fluency");
  const hasInterpretation = selectedOptions.some(
    (item) => item.key === "interpretation"
  );

  const updateSelected = (changes) => {
    setSelectedTypes((previous) =>
      previous.map((item) =>
        item.key === "direct_translation" ? { ...item, ...changes } : item
      )
    );
  };

  const handleOptionClick = (option) => {
    const isSelected = selectedOptions.some(
      (item) => item.key === option.key
    );
    // 원래 순서를 지키려고 전체 목록에서 걸러 낸다. 누른 차례대로 담으면 학생이
    // 거치는 순서가 선생님마다 달라진다.
    const nextOptions = allOptions.filter((candidate) =>
      candidate.key === option.key
        ? !isSelected
        : selectedOptions.some((item) => item.key === candidate.key)
    );

    // **말하는 단계를 고르면 읽기 채점을 함께 켠다.** 채점이 꺼져 있으면 아이가
    // 소리 내어 읽어도 아무 일이 일어나지 않고 그냥 다음으로 넘어간다. 유창성과
    // 통역은 채점이 있어야 성립하는 단계라, 골라 놓고 꺼 두면 빈 껍데기가 된다.
    const willSpeak = nextOptions.some((item) =>
      SPEAKING_MODE_LIST.includes(item.key)
    );

    updateSelected({
      options: nextOptions,
      is_pronunciation_check_enabled: willSpeak
        ? true
        : isPronunciationCheckEnabled,
    });
  };

  useEffect(() => {
    if (isReadOnly || selectedOptions.length > 0) return;
    updateSelected({
      options: allOptions,
      speech_rate: DEFAULT_SPEECH_RATE,
      reading_unit: DEFAULT_READING_UNIT,
      fluency_passing_score: DEFAULT_PASSING_SCORE,
      interpretation_passing_score: DEFAULT_PASSING_SCORE,
      is_pronunciation_check_enabled: allOptions.some((item) =>
        SPEAKING_MODE_LIST.includes(item.key)
      ),
    });
    // eslint-disable-next-line
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <SubTitle>단계</SubTitle>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {allOptions.map((option) => {
            const isSelected = selectedOptions.some(
              (item) => item.key === option.key
            );
            return (
              <div
                key={option.key}
                onClick={() => !isReadOnly && handleOptionClick(option)}
                style={pillStyle(isSelected, isReadOnly)}
                title={MODE_DESCRIPTION[option.key]}
              >
                {option.value}
                {PREMIUM_MODE_LIST.includes(option.key) && (
                  <PremiumBadge onClick={() => alert(PREMIUM_NOTICE)} />
                )}
              </div>
            );
          })}
        </div>
        <div
          style={{ fontSize: "0.74rem", color: "#7A8490", marginTop: "7px" }}
        >
          고른 단계를 순서대로 거칩니다.
          {selectedOptions.length > 0 &&
            ` (${selectedOptions.map((item) => item.value).join(" → ")})`}
        </div>
      </div>

      <div>
        <SubTitle>읽는 단위</SubTitle>
        {/* 단계마다 다르게 정할 수 있다 (2026-08-27). 유창성은 문장 단위로 통으로
            읽히고 통역은 끊어읽기로 잘게 옮기게 하는 식이다. 값이 없는 단계는
            공통 reading_unit(기본 끊어읽기)을 따르므로 옛 출제와 호환된다. */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {selectedOptions.map((option) => {
            const unitOfStage =
              (selected?.reading_unit_by_mode || {})[option.key] ?? readingUnit;
            return (
              <div
                key={option.key}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    width: "52px",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: "#3A434E",
                  }}
                >
                  {option.value}
                </span>
                {allReadingUnits.map((unit) => (
                  <div
                    key={unit.key}
                    onClick={() => {
                      if (isReadOnly) return;
                      const nextByMode = {
                        ...(selected?.reading_unit_by_mode || {}),
                        [option.key]: unit.key,
                      };
                      // 옛 학생 앱은 공통 값 하나만 본다. 전 단계가 같은 단위면
                      // 공통 값도 그것으로 맞춰 두어 되도록 같은 경험이 되게 한다.
                      const unitSet = new Set(
                        selectedOptions.map(
                          (one) => nextByMode[one.key] ?? readingUnit
                        )
                      );
                      updateSelected({
                        reading_unit_by_mode: nextByMode,
                        ...(unitSet.size === 1
                          ? { reading_unit: [...unitSet][0] }
                          : {}),
                      });
                    }}
                    style={pillStyle(unitOfStage === unit.key, isReadOnly)}
                  >
                    {unit.value}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div
          style={{ fontSize: "0.74rem", color: "#7A8490", marginTop: "7px" }}
        >
          끊어읽기 단위는 슬래시로 나뉜 덩어리를 하나씩 읽힙니다. 단계마다 다르게
          정할 수 있어요 (예: 유창성은 문장 단위, 통역은 끊어읽기 단위).
        </div>
      </div>

      <div>
        <SubTitle>읽기 속도</SubTitle>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {allSpeechRates.map((rate) => (
            <div
              key={rate.key}
              onClick={() =>
                !isReadOnly && updateSelected({ speech_rate: rate.key })
              }
              style={pillStyle(speechRate === rate.key, isReadOnly)}
            >
              {rate.value}
            </div>
          ))}
        </div>
      </div>

      {(hasFluency || hasInterpretation) && (
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <SubTitle>통과 기준</SubTitle>
          {hasFluency && (
            <ScoreRow
              label="유창성"
              description="읽기 정확도가 이 점수 이상이면 넘어갑니다"
              value={fluencyPassingScore}
              scoreList={allPassingScores}
              isReadOnly={isReadOnly}
              onChange={(score) =>
                updateSelected({ fluency_passing_score: score })
              }
            />
          )}
          {hasInterpretation && (
            <ScoreRow
              label="통역"
              description="통역 점수가 이 점수 이상이면 넘어갑니다"
              value={interpretationPassingScore}
              scoreList={allPassingScores}
              isReadOnly={isReadOnly}
              onChange={(score) =>
                updateSelected({ interpretation_passing_score: score })
              }
            />
          )}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <SubTitle>읽기 확인</SubTitle>
        <ToggleRow
          title="읽기 채점"
          description={
            isPronunciationCheckEnabled
              ? "학생이 읽으면 얼마나 맞게 읽었는지 바로 채점합니다"
              : "채점 없이 따라 읽기만 합니다"
          }
          isOn={isPronunciationCheckEnabled}
          isReadOnly={isReadOnly}
          onClick={() =>
            updateSelected({
              is_pronunciation_check_enabled: !isPronunciationCheckEnabled,
            })
          }
        />
      </div>
    </div>
  );
};

export default DirectTranslationSelection;
